import * as yup from 'yup';
import { AlertTriangle, Check, ExternalLink, XCircle } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { gql } from '__generated__';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';

import {
  GetIntegrationsForProjectQuery as GetIntegrationsForProject, IntegrationType, Permission,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../common/Common/OptionsDropdown';
import { DisconnectAppMutation } from './ApiPage';
import {
  assetUrl, facebookPixelRegex, getServerErrors, googleAnalyticsRegex, resizedImageUrl,
} from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import AddActiveCampaignIntegrationForm from './AddActiveCampaignIntegrationForm';
import DeleteIntegrationMutation from './DeleteIntegrationMutation';
import UI from '../../common/UI';
import config from '../config';
import useConfirmModal from '../../common/useConfirmModal';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

type Project = GetIntegrationsForProject['project'];
type App = Project['app_authorizations'][0]['app'];

const editIntegrationsSchema = yup.object().shape({
  google_analytics: yup.string().ensure().matches(googleAnalyticsRegex, {
    excludeEmptyString: true,
    name: 'ga_code',
  }),
  facebook_pixel: yup.string().ensure().matches(facebookPixelRegex, {
    excludeEmptyString: true,
    name: 'fbp_code',
  }),
});

const EditIntegrationsMutation = gql(`
  mutation EditIntegrations($input: EditIntegrationsInput!) {
    editIntegrations(input: $input) {
      id
      google_analytics
      facebook_pixel
    }
  }
`);

const getValues = (project: any) => ({
  google_analytics: project.google_analytics,
  facebook_pixel: project.facebook_pixel,
});

gql(`
  fragment Integrations on Project {
    integrations {
      id
      type
      title
      deletable
      mailchimp {
        id
        account_name
      }
      active_campaign {
        id
        account_name
      }
    }
  }
`);

type Integration = GetIntegrationsForProject['project']['integrations'][0];

const getExternalApps = (project: Project) => (
  project.app_authorizations.filter((auth) => auth.app.project.id !== project.id)
);

export const GetIntegrationsForProjectQuery = gql(`
  query GetIntegrationsForProject($id: ID!) {
    project(id: $id) {
      id
      organisation_name
      google_analytics
      facebook_pixel
      ...Integrations
      app_authorizations {
        id
        permissions
        app {
          id
          name
          logo_url
          requested_permissions
          project {
            id
            organisation_name
          }
        }
      }
    }
  }
`);

interface EditIntegrationsPageProps {
  projectId: string;
}

const EditIntegrationsPage = ({ projectId }: EditIntegrationsPageProps) => {
  const { data } = useQuery(GetIntegrationsForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return null;
  }

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      {getExternalApps(project).length > 0 && (
        <>
          <ConnectedAppsSection project={project} />
          <UI.HR />
        </>
      )}
      <AnalyticsSection project={project} />
      <UI.HR />
      <EmailMarketingSection project={project} />
    </UI.Container>
  );
};

const ConnectedAppsSection = ({ project }: GetIntegrationsForProject & {}) => {
  const { t } = useTranslation();

  const externalAppAuthorizations = getExternalApps(project);

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.H3>
          {t('connected_apps')}
        </UI.H3>

        <UI.InputDescription sc={{ mt: 1 }}>
          {t('connected_apps_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card>
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {externalAppAuthorizations.map(({ app, permissions, id }) => (
            <ConnectedApp app={app} project={project} permissions={permissions} key={id} />
          ))}
        </UI.GridContainer>
      </UI.Card>
    </UI.FormGrid>
  );
};

interface ConnectedAppProps {
  permissions: Permission[];
  project: Project;
  app: App;
}

const ConnectedApp = ({ app, project, permissions }: ConnectedAppProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [disconnectApp] = useMutation(
    DisconnectAppMutation,
    {
      variables: {
        project_id: project.id,
        app_id: app.id,
      },
      refetchQueries: ['GetIntegrationsForProject'],
      onCompleted: () => {
        notifier.success(t('notifications.app_removed'));
      },
    },
  );

  const [confirmDisconnect] = useConfirmModal({
    title: t('disconnect'),
    body: (
      <Trans
        i18nKey="confirm_disconnect_app"
        values={{
          name: app.name,
          project: project.organisation_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => disconnectApp(),
  }, []);

  return (
    <ContainerWithOptions
      name={app.name}
      alignVertical="center"
      renderOptions={() => (
        <Option
          icon={<XCircle />}
          label={t('disconnect')}
          aria-label={t('disconnect')}
          onClick={() => confirmDisconnect()}
        />
      )}
    >
      <UI.GridContainer sc={{ columns: [undefined, '36px 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div>
          <UI.ProfilePic
            sc={{ background: app.logo_url ? 'white' : 'secondary', fit: 'cover', size: 36 }}
          >
            {app.logo_url && (
              <img src={resizedImageUrl(app.logo_url, 300)} alt={app.name} />
            )}
            {!app.logo_url && (
              <UI.Span><UI.Span>{app.name.charAt(0).toUpperCase()}</UI.Span></UI.Span>
            )}
          </UI.ProfilePic>
        </UI.Div>
        <UI.Div>
          <UI.H4>{app.name}</UI.H4>
          {app.project.organisation_name}
        </UI.Div>
        <UI.Div sc={{ textAlign: ['right', 'left'] }}>
          {app.requested_permissions.map((permission) => (
            <UI.Span key={permission}>
              {permissions.indexOf(permission) === -1 ? (
                <UI.Label
                  sc={{ small: true, background: 'error.100', color: 'error.700', ml: 1 }}
                >
                  <UI.Icon>
                    <AlertTriangle />
                  </UI.Icon>
                  {' '}
                  <UI.Span style={{ textDecoration: 'line-through' }}>
                    {t(`auth_permissions.${permission}`)}
                  </UI.Span>
                </UI.Label>
              ) : (
                <UI.Label
                  sc={{ small: true, ml: 1 }}
                >
                  <UI.Icon>
                    <Check />
                  </UI.Icon>
                  {' '}
                  <UI.Span>
                    {t(`auth_permissions.${permission}`)}
                  </UI.Span>
                </UI.Label>
              )}
            </UI.Span>
          ))}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

const AnalyticsSection = ({ project }: GetIntegrationsForProject) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: getValues(project),
  });

  const [editIntegrations, { error, loading }] = useMutation(
    EditIntegrationsMutation, {
      variables: {
        input: {
          ...form.values,
          project: {
            id: project.id,
          },
        },
      },
      refetchQueries: ['Session'],
      onCompleted: ({ editIntegrations: project }) => {
        notifier.success(t('notifications.project_saved'));

        form.reset(getValues(project));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editIntegrationsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  if (!project) {
    return null;
  }

  return (
    <UI.Form onSubmit={() => editIntegrations()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H3>{t('tracking_and_analytics')}</UI.H3>

          <UI.InputDescription sc={{ mt: 1 }}>
            {t('tracking_and_analytics_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.Card>
          <UI.FormGrid>
            <UI.InputGroup
              sc={{ valid: !errors.google_analytics, touched: form.touched.google_analytics }}
            >
              <UI.InputLabel>
                <UI.FlexContainer sc={{ alignItems: 'center' }}>
                  <UI.Span sc={{ mr: 1 }}>
                    <img
                      src={assetUrl('images/integrations/googleanalytics.svg')}
                      width={16}
                      alt={t('google_analytics')}
                    />
                  </UI.Span>
                  {t('google_analytics')}
                </UI.FlexContainer>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('enhanced_ecommerce_description')}
                {' '}
                <UI.A
                  href="https://about.atleta.cc/article/conversion-tracking-using-google-analytics"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('read_about_google_analytics')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
              <UI.Input {...form.text('google_analytics')} placeholder="G-ABCDE12345 or UA-12345678-1" />
              <UI.ErrorMessages
                attribute={t('google_analytics')}
                errors={errors.google_analytics}
              />
            </UI.InputGroup>

            <UI.InputGroup
              sc={{ valid: !errors.facebook_pixel, touched: form.touched.facebook_pixel }}
            >
              <UI.InputLabel>
                <UI.FlexContainer sc={{ alignItems: 'center' }}>
                  <UI.Span sc={{ mr: 1 }}>
                    <img src={assetUrl('images/integrations/meta.svg')} width={20} alt={t('meta')} />
                  </UI.Span>
                  {t('meta_pixel')}
                </UI.FlexContainer>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('meta_pixel_description')}
                {' '}
                <UI.A
                  href="https://about.atleta.cc/article/conversion-tracking-using-facebook-pixel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('read_about_meta_pixel')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
              <UI.Input {...form.text('facebook_pixel')} placeholder="112233445566778899" />
              <UI.ErrorMessages
                attribute={t('meta_pixel')}
                errors={errors.facebook_pixel}
              />
            </UI.InputGroup>

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.Card>
      </UI.FormGrid>
    </UI.Form>
  );
};

const EmailMarketingSection = ({ project }: GetIntegrationsForProject) => {
  const { t } = useTranslation();

  const mailchimpIntegrations = project.integrations.filter(
    (integration) => integration.type === IntegrationType.mailchimp,
  );
  const activeCampaignIntegrations = project.integrations.filter(
    (integration) => integration.type === IntegrationType.active_campaign,
  );

  const [openConnectModal, closeConnectModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeConnectModal}>
      <AddActiveCampaignIntegrationForm
        onSuccess={closeConnectModal}
        onCancel={closeConnectModal}
      />
    </UI.Modal>
  ), []);

  const hasIntegrations = mailchimpIntegrations.length > 0 || activeCampaignIntegrations.length > 0;

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.H3>{t('email_marketing')}</UI.H3>

        <UI.InputDescription sc={{ mt: 1 }}>
          {t('email_marketing_integrations_description')}
        </UI.InputDescription>

      </UI.Div>
      <UI.Card>
        <UI.GridContainer>
          {hasIntegrations && (
            <UI.GridContainer sc={{ gutter: 0.5 }}>
              {project.integrations.map((integration) => {
                if (integration.active_campaign) {
                  return <ActiveCampaignIntegration integration={integration} key={integration.id} />;
                }

                if (integration.mailchimp) {
                  return <MailchimpIntegration integration={integration} key={integration.id} />;
                }

                return null;
              })}
            </UI.GridContainer>
          )}
          {hasIntegrations && <UI.HR />}
          <UI.FlexContainer sc={{ spaceX: [0, 1], spaceY: [1, 0], direction: ['column', 'row'] }}>
            <UI.Button type="button" onClick={() => openConnectModal()}>
              {t('connect_with', { title: t('active_campaign') })}
            </UI.Button>
            <UI.AButton
              href={`${config.backendPath}/api/mailchimp/authorize/${project.id}`}
            >
              {t('connect_with', { title: t('mailchimp') })}
            </UI.AButton>
          </UI.FlexContainer>
        </UI.GridContainer>
      </UI.Card>
    </UI.FormGrid>
  );
};

export interface MailchimpIntegrationProps {
  integration: Integration;
}

const MailchimpIntegration = ({ integration }: MailchimpIntegrationProps) => {
  const { t } = useTranslation();

  const [disconnect, { loading }] = (
    useMutation(DeleteIntegrationMutation, {
      variables: {
        id: integration.id,
      },
      refetchQueries: ['GetIntegrationsForProject'],
    })
  );

  const [confirmDisconnect] = useConfirmModal({
    title: t('disconnect'),
    body: (
      <Trans
        i18nKey="confirm_disconnect_integration"
        values={{
          name: integration.mailchimp.account_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => disconnect(),
  }, []);

  return (
    <ContainerWithOptions
      name={integration.mailchimp.account_name}
      alignVertical="center"
      renderOptions={() => (
        integration.deletable ? (
          <Option
            icon={<XCircle />}
            label={t('disconnect')}
            aria-label={t('disconnect')}
            onClick={() => confirmDisconnect()}
            disabled={loading}
          />
        ) : (
          <Option
            icon={<XCircle />}
            label={t('disconnect')}
            aria-label={t('disconnect')}
            disabled
            onClick={() => undefined}
          />
        )
      )}
    >
      <UI.GridContainer sc={{ columns: '3fr 2fr', alignVertical: 'center', muted: loading }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4>
            <UI.Truncate>
              {integration.mailchimp.account_name}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        <UI.Div sc={{ textAlign: ['right', 'left'] }}>
          {t('mailchimp')}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export interface ActiveCampaignIntegrationProps {
  integration: Integration;
}

const ActiveCampaignIntegration = ({ integration }: ActiveCampaignIntegrationProps) => {
  const { t } = useTranslation();

  const [disconnect, { loading }] = (
    useMutation(DeleteIntegrationMutation, {
      variables: {
        id: integration.id,
      },
      refetchQueries: ['GetIntegrationsForProject'],
    })
  );

  const [confirmDisconnect] = useConfirmModal({
    title: t('disconnect'),
    body: (
      <Trans
        i18nKey="confirm_disconnect_integration"
        values={{
          name: integration.active_campaign.account_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => disconnect(),
  }, []);

  return (
    <ContainerWithOptions
      name={integration.active_campaign.account_name}
      alignVertical="center"
      renderOptions={() => (
        integration.deletable ? (
          <Option
            icon={<XCircle />}
            label={t('disconnect')}
            aria-label={t('disconnect')}
            onClick={() => confirmDisconnect()}
            disabled={loading}
          />
        ) : (
          <Option
            icon={<XCircle />}
            label={t('disconnect')}
            aria-label={t('disconnect')}
            disabled
            onClick={() => undefined}
          />
        )
      )}
    >
      <UI.GridContainer sc={{ columns: '3fr 2fr', alignVertical: 'center', muted: loading }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4>
            <UI.Truncate>
              {integration.active_campaign.account_name}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        <UI.Div sc={{ textAlign: ['right', 'left'] }}>
          {t('active_campaign')}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export default EditIntegrationsPage;
